import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface PageMetaProps {
  title: string;
  description: string;
  ogImage?: string;
  type?: string;
  keywords?: string;
  noIndex?: boolean;
}

const BASE_URL = "https://tourbi.tech";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;

const setMetaTag = (attr: string, key: string, content: string) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export const usePageMeta = ({
  title,
  description,
  ogImage,
  type = "website",
  keywords,
  noIndex = false,
}: PageMetaProps) => {
  const { pathname } = useLocation();
  const fullUrl = `${BASE_URL}${pathname === "/" ? "" : pathname}`;
  const image = ogImage || DEFAULT_OG_IMAGE;

  useEffect(() => {
    // Title
    document.title = title;

    // Standard meta
    setMetaTag("name", "description", description);
    if (keywords) {
      setMetaTag("name", "keywords", keywords);
    }

    // Robots
    if (noIndex) {
      setMetaTag("name", "robots", "noindex, nofollow");
    } else {
      const existing = document.querySelector('meta[name="robots"]');
      if (existing) existing.remove();
    }

    // Open Graph
    setMetaTag("property", "og:title", title);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:url", fullUrl);
    setMetaTag("property", "og:image", image);
    setMetaTag("property", "og:type", type);
    setMetaTag("property", "og:site_name", "Tourbi");
    setMetaTag("property", "og:locale", "en_US");

    // Twitter Card
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:site", "@Tourbi");
    setMetaTag("name", "twitter:title", title);
    setMetaTag("name", "twitter:description", description);
    setMetaTag("name", "twitter:image", image);
  }, [title, description, fullUrl, image, type, keywords, noIndex]);
};
